import type { Node, PageSettings, ReportTemplate, ConditionGroup, FlowOptions } from "../types/template.js";
import type { BlockStyle } from "../types/style.js";
import { CURRENT_SCHEMA_VERSION } from "../types/template.js";
import { defaultDocumentStyles } from "../theme/default-theme.js";
import { createId } from "./ids.js";
import type { BlockRegistry } from "../registry/registry.js";
import { resolveDefaultProps } from "../registry/block-definition.js";

export function defaultPageSettings(overrides: Partial<PageSettings> = {}): PageSettings {
  return {
    size: "A4",
    orientation: "portrait",
    margins: { top: 18, right: 18, bottom: 18, left: 18 },
    header: { height: 14, differentFirstPage: false },
    footer: { height: 12, differentFirstPage: false },
    ...overrides
  };
}

export interface CreateTemplateOptions {
  id?: string;
  name?: string;
  page?: Partial<PageSettings>;
  locale?: string;
  currency?: string;
  /** Start with an empty first section. Default true. */
  withEmptySection?: boolean;
}

/** Creates a new, valid, empty template. */
export function createTemplate(opts: CreateTemplateOptions = {}): ReportTemplate {
  const now = new Date().toISOString();
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: opts.id ?? createId("tpl"),
    name: opts.name ?? "Untitled report",
    meta: { createdAt: now, updatedAt: now },
    page: defaultPageSettings(opts.page),
    styles: defaultDocumentStyles(),
    header: { default: [] },
    footer: { default: [] },
    body: opts.withEmptySection === false ? [] : [createSection()],
    settings: { locale: opts.locale ?? "en-GB", currency: opts.currency ?? "GBP" }
  };
}

export interface CreateNodeOptions {
  id?: string;
  props?: Record<string, unknown>;
  style?: BlockStyle;
  children?: Node[];
  flow?: FlowOptions;
  visibleWhen?: string | ConditionGroup;
  name?: string;
}

/** Creates a node of a registered block type with its default props. */
export function createNode(registry: BlockRegistry, type: string, opts: CreateNodeOptions = {}): Node {
  const def = registry.get(type);
  const props = { ...(def ? resolveDefaultProps(def) : {}), ...(opts.props ?? {}) };
  const node: Node = { id: opts.id ?? createId(), type, props };
  if (opts.style) node.style = opts.style;
  if (def?.container || opts.children) node.children = opts.children ?? [];
  if (opts.flow) node.flow = opts.flow;
  if (opts.visibleWhen !== undefined) node.visibleWhen = opts.visibleWhen;
  if (opts.name) node.name = opts.name;
  return node;
}

export function createSection(opts: CreateNodeOptions = {}): Node {
  return { id: opts.id ?? createId("sec"), type: "section", props: { padding: 0, ...(opts.props ?? {}) }, children: opts.children ?? [], ...(opts.style ? { style: opts.style } : {}), ...(opts.flow ? { flow: opts.flow } : {}), ...(opts.name ? { name: opts.name } : {}) };
}

export function createColumn(span: number, children: Node[] = [], id?: string): Node {
  return { id: id ?? createId("col"), type: "column", props: { span }, children };
}

/** Creates a row with the given twelfths layout, e.g. [6, 6] or [4, 8]. */
export function createRow(layout: number[], columns?: Node[][], opts: CreateNodeOptions = {}): Node {
  const total = layout.reduce((a, b) => a + b, 0);
  if (total !== 12) throw new Error(`Row layout must sum to 12, got ${total} from [${layout.join(", ")}]`);
  return {
    id: opts.id ?? createId("row"),
    type: "row",
    props: { layout, ...(opts.props ?? {}) },
    children: layout.map((span, i) => createColumn(span, columns?.[i] ?? [])),
    ...(opts.style ? { style: opts.style } : {}),
    ...(opts.flow ? { flow: opts.flow } : {}),
    ...(opts.name ? { name: opts.name } : {})
  };
}

export const ROW_LAYOUTS: Array<{ id: string; label: string; layout: number[] }> = [
  { id: "12", label: "Full width", layout: [12] },
  { id: "6-6", label: "Two columns", layout: [6, 6] },
  { id: "4-4-4", label: "Three columns", layout: [4, 4, 4] },
  { id: "3-3-3-3", label: "Four columns", layout: [3, 3, 3, 3] },
  { id: "8-4", label: "Wide left", layout: [8, 4] },
  { id: "4-8", label: "Wide right", layout: [4, 8] },
  { id: "9-3", label: "Sidebar right", layout: [9, 3] },
  { id: "3-9", label: "Sidebar left", layout: [3, 9] }
];

/** Deep-clones a subtree assigning fresh ids (for duplicate/paste/library insert). */
export function cloneWithNewIds(node: Node): Node {
  const prefix = node.type === "section" ? "sec" : node.type === "row" ? "row" : node.type === "column" ? "col" : "n";
  return {
    ...structuredClone(node),
    id: createId(prefix),
    children: node.children?.map(cloneWithNewIds)
  };
}
